import { type Hls } from '@/scripts/global';

interface Slide {
  index: number;
  panel: HTMLElement;
  poster: HTMLImageElement | null;
  video: HTMLVideoElement;
  mp4?: string;
  hls?: string;
  duration: number;
  loaded: boolean;
  paused: boolean;
  hlsInstance?: InstanceType<Hls['default']>;
}

/**
 * Dynamic Class Types (Split) orchestrator (ECOMM-3909).
 *
 * Autorotates across class-type videos: a slide advances when its clip ends,
 * with an optional max-duration cap as a backstop. Clicking a title jumps to
 * that slide. The mute/unmute choice is global and persists across rotations.
 */
export function initDynamicClassTypes(): void {
  const roots = document.querySelectorAll<HTMLElement>('[data-dynamic-class-types]');
  roots.forEach((root) => new DynamicClassTypes(root));
}

class DynamicClassTypes {
  private readonly root: HTMLElement;
  private readonly slides: Slide[] = [];
  private readonly titles: HTMLButtonElement[];
  private readonly unmuteBtn: HTMLButtonElement | null;
  private readonly playpauseBtn: HTMLButtonElement | null;
  private readonly playpauseLabel: HTMLElement | null;
  private current = 0;
  private unmuted = false;
  private maxDuration = 0;
  private autoplay = true;
  private loop = true;
  private capTimer: number | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.maxDuration = Number(root.getAttribute('data-max-duration') || 0);
    this.autoplay = root.getAttribute('data-autoplay') !== 'false';
    this.loop = root.getAttribute('data-loop') !== 'false';

    this.titles = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-dct-jump]'));
    this.unmuteBtn = root.querySelector<HTMLButtonElement>('[data-dct-unmute]');
    this.playpauseBtn = root.querySelector<HTMLButtonElement>('[data-dct-playpause]');
    this.playpauseLabel = root.querySelector<HTMLElement>('[data-dct-playpause-label]');

    root.querySelectorAll<HTMLElement>('.dct__media').forEach((panel, index) => {
      const video = panel.querySelector<HTMLVideoElement>('.dct__video');
      if (!video) return;
      this.slides.push({
        index,
        panel,
        poster: panel.querySelector<HTMLImageElement>('.dct__poster'),
        video,
        mp4: video.getAttribute('data-mp4-src') || undefined,
        hls: video.getAttribute('data-hls-src') || undefined,
        duration: Number(panel.getAttribute('data-duration') || 0),
        loaded: false,
        paused: !this.autoplay,
      });
    });

    this.bindEvents();

    if (this.slides.length > 0) {
      if (this.autoplay) {
        void this.activate(0);
      } else {
        this.syncUi(0);
        void this.loadSource(this.slides[0]);
      }
    }
    this.syncPlayPauseUi();
  }

  private bindEvents(): void {
    this.titles.forEach((btn) => {
      const index = Number(btn.getAttribute('data-dct-jump'));
      if (Number.isNaN(index)) return;
      btn.addEventListener('click', () => {
        void this.activate(index);
      });
    });

    this.unmuteBtn?.addEventListener('click', () => {
      this.toggleMute();
    });

    this.playpauseBtn?.addEventListener('click', () => {
      this.togglePlayPause();
    });

    this.root.querySelector<HTMLElement>('.dct__media-panel')?.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('.dct__controls')) return;
      this.togglePlayPause();
    });

    this.slides.forEach((slide) => {
      slide.video.addEventListener('ended', () => {
        if (slide.index === this.current) {
          if (this.loop || this.current < this.slides.length - 1) {
            this.advance();
          }
        }
      });

      slide.video.addEventListener('playing', () => {
        if (slide.index === this.current) {
          slide.panel.classList.add('is-playing');
        }
      });

      slide.video.addEventListener('loadeddata', () => {
        if (slide.index === this.current) {
          slide.panel.classList.add('is-playing');
        }
      });
    });
  }

  private async activate(index: number): Promise<void> {
    if (this.slides.length === 0 || index < 0 || index >= this.slides.length) return;

    this.clearCapTimer();

    const previous = this.slides[this.current];
    if (previous && previous.index !== index) {
      previous.video.pause();
    }

    this.current = index;
    const slide = this.slides[index];

    this.syncUi(index);

    await this.loadSource(slide);

    slide.video.muted = !this.unmuted;
    if (slide.paused) {
      slide.video.pause();
    } else {
      try {
        await slide.video.play();
      } catch {
        // Autoplay may be blocked until the first user interaction.
      }
      this.armCapTimer();
    }

    this.syncPlayPauseUi();
  }

  private syncUi(index: number): void {
    const slide = this.slides[index];
    if (!slide) return;

    this.slides.forEach((s) => {
      s.panel.classList.toggle('is-active', s.index === index);
    });

    this.titles.forEach((btn, i) => {
      const active = i === index;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  private advance(): void {
    this.clearCapTimer();
    const next = (this.current + 1) % this.slides.length;
    void this.activate(next);
  }

  private armCapTimer(): void {
    const slide = this.slides[this.current];
    const duration = slide && slide.duration > 0 ? slide.duration : this.maxDuration;
    if (duration > 0) {
      this.capTimer = window.setTimeout(() => {
        this.advance();
      }, duration * 1000);
    }
  }

  private clearCapTimer(): void {
    if (this.capTimer !== null) {
      window.clearTimeout(this.capTimer);
      this.capTimer = null;
    }
  }

  private async loadSource(slide: Slide): Promise<void> {
    if (slide.loaded) return;
    slide.loaded = true;

    if (slide.hls) {
      const mm = window.Ucoast?.mediaManager;
      if (mm && mm.hlsRequired === false) {
        slide.video.src = slide.hls;
        return;
      }

      await this.waitForHlsLibrary(mm);

      if (mm?.Hls) {
        try {
          slide.hlsInstance = new mm.Hls.default();
          slide.hlsInstance.on('hlsManifestParsed', () => {
            /* noop: source is already assigned */
          });
          slide.video.src = slide.hls;
          await slide.hlsInstance.loadSource(slide.hls);
          await slide.hlsInstance.attachMedia(slide.video);
        } catch {
          slide.video.src = slide.hls;
        }
      } else {
        slide.video.src = slide.hls;
      }
    } else if (slide.mp4) {
      slide.video.src = slide.mp4;
    }
  }

  private async waitForHlsLibrary(mm: { hlsLibraryLoaded: boolean } | undefined): Promise<void> {
    if (!mm) return;
    if (mm.hlsLibraryLoaded) return;
    for (let i = 0; i < 100; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (mm.hlsLibraryLoaded) return;
    }
  }

  private togglePlayPause(): void {
    const slide = this.slides[this.current];
    if (!slide) return;

    if (slide.paused) {
      slide.paused = false;
      slide.video.muted = !this.unmuted;
      void slide.video.play().catch(() => {
        // Play may be blocked until user interaction; keep state consistent.
      });
      this.armCapTimer();
    } else {
      slide.paused = true;
      slide.video.pause();
      this.clearCapTimer();
    }

    this.syncPlayPauseUi();
  }

  private syncPlayPauseUi(): void {
    const slide = this.slides[this.current];
    const playing = slide ? !slide.paused : false;
    this.playpauseBtn?.setAttribute('aria-pressed', String(playing));
    this.playpauseBtn?.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    if (this.playpauseLabel) {
      this.playpauseLabel.textContent = playing ? 'Pause' : 'Play';
    }
  }

  private toggleMute(): void {
    this.unmuted = !this.unmuted;
    this.slides.forEach((slide) => {
      slide.video.muted = !this.unmuted;
    });
    this.unmuteBtn?.setAttribute('aria-pressed', String(this.unmuted));
    this.unmuteBtn?.setAttribute('aria-label', this.unmuted ? 'Mute' : 'Unmute');

    if (this.unmuted && !this.slides[this.current]?.paused) {
      const slide = this.slides[this.current];
      if (slide) {
        void slide.video.play();
      }
    }
  }
}
